import {
  ACTIVE_CASE_STATUSES,
  CARE_PLAN_CASE_STATUS_LABEL,
  CarePlanCase,
  CarePlanCaseStatus,
} from '../care-plan.model';

/**
 * Each dimension is a single optional selection. `status: null` means "no
 * explicit status filter" — the case list still defaults to
 * {@link ACTIVE_CASE_STATUSES} (Closed + Suggested excluded) until the FA
 * explicitly filters to a specific status.
 */
export interface CarePlanCaseFilters {
  status: CarePlanCaseStatus | null;
  domain: string | null;
  goalId: string | null;
}

export const DEFAULT_CASE_FILTERS: CarePlanCaseFilters = {
  status: null,
  domain: null,
  goalId: null,
};

export function isDefaultCaseFilters(filters: CarePlanCaseFilters): boolean {
  return (
    filters.status === null &&
    filters.domain === null &&
    filters.goalId === null
  );
}

export function applyCaseFilters(
  cases: readonly CarePlanCase[],
  filters: CarePlanCaseFilters,
  live = false,
): CarePlanCase[] {
  return cases.filter((c) => {
    if (filters.status) {
      if (c.status !== filters.status) return false;
    } else if (!live && !ACTIVE_CASE_STATUSES.includes(c.status)) {
      return false;
    }
    if (filters.domain && c.domain !== filters.domain) return false;
    if (filters.goalId && !c.goalIds.includes(filters.goalId)) return false;
    return true;
  });
}

/** An explicit filter has no visible matches; default-filter misses belong to the Empty state. */
export function isFilterMiss(
  allCases: readonly CarePlanCase[],
  filters: CarePlanCaseFilters,
  live = false,
): boolean {
  return (
    !isDefaultCaseFilters(filters) &&
    applyCaseFilters(allCases, filters, live).length === 0
  );
}

export interface CarePlanCaseFilterChip {
  dimension: 'status' | 'domain' | 'goal';
  label: string;
}

export function buildCaseFilterChips(
  filters: CarePlanCaseFilters,
  labels: {
    statusLabel?(status: string): string;
    domainLabel(key: string): string;
    goalLabel(goalId: string): string;
  },
): CarePlanCaseFilterChip[] {
  const chips: CarePlanCaseFilterChip[] = [];
  if (filters.status) {
    chips.push({
      dimension: 'status',
      label:
        labels.statusLabel?.(filters.status) ??
        CARE_PLAN_CASE_STATUS_LABEL[filters.status] ??
        filters.status,
    });
  }
  if (filters.domain) {
    chips.push({
      dimension: 'domain',
      label: labels.domainLabel(filters.domain),
    });
  }
  if (filters.goalId) {
    chips.push({ dimension: 'goal', label: labels.goalLabel(filters.goalId) });
  }
  return chips;
}
